import React, { useMemo, useRef } from 'react';
import { useAppSelector } from 'hooks';
import { Col, Row } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query';
import dayjs from 'dayjs';

import { dateDisplayFormat } from '~libs/dayjs';
import { showCaseManagement } from '~support-at-home/lib/derive';

import { TableView, TableViewHandle } from '~components/table-view-v3';

import { Budget, COPY_STATUS, Section } from '~support-at-home/models';

import { columnsFactory } from '~support-at-home/forms/budgets/sectionItemsList';

import { useGetFilteredServicesQuery } from '~support-at-home/actions/budgets';

import { selectLookupCalendarFrequencies } from '~lookups/selectors/calendarFrequencies';
import { selectLookupShifts } from '~lookups/selectors/shifts';
import { selectClientByID } from '~main/selectors/clients/clients';
import { selectPricePlanRevisions } from '~support-at-home/selectors/pricePlanRevisions';
import { selectPricePlans } from '~support-at-home/selectors/pricePlans';

import BudgetGrandTotal from './BudgetGrandTotal';
import SectionTotal from './BudgetSectionTotal';
import BudgetSupplementTotal from './BudgetSupplementTotal';

interface ComponentProps {
  value: Partial<Budget>;
}

const BudgetReview: React.FC<ComponentProps> = ({ value }) => {
  const pricePlans = useAppSelector(selectPricePlans);
  const pricePlanRevisions = useAppSelector(selectPricePlanRevisions);
  const {
    package_fixed_id,
    client_id = 0,
    price_plan_id,
    price_plan_revision_id,
    start_date,
    end_date,
    sections = [],
    total_amount,
    total_includes_case_management = false,
    grand_total = { cost: 0, contribution: 0, govSubsidy: 0, case_management: 0 },
    supplements,
    ignore_sah_filters,
  } = value || {};

  const shifts = useAppSelector(selectLookupShifts);
  const calendarFrequencies = useAppSelector(selectLookupCalendarFrequencies);
  const clientId = typeof client_id === 'string' ? parseInt(client_id, 10) : client_id;
  const pkgFixedId = typeof package_fixed_id === 'string' ? parseInt(package_fixed_id, 10) : package_fixed_id;

  const { data: filteredServices } = useGetFilteredServicesQuery(
    price_plan_revision_id
      ? {
          revision_id: price_plan_revision_id,
          client_id: clientId,
          package_fixed_id: pkgFixedId,
          ignore_sah_filters: ignore_sah_filters ?? false,
        }
      : skipToken,
  );

  const client = useAppSelector((state) => selectClientByID(state, client_id));
  const findAndBuild = (lookup: any[], id: any) => {
    const item = lookup.find((item) => item.id === id);
    return item && (item.name || item.label || '');
  };

  const findAndBuildRevision = (lookup: any, id: any) => {
    return `Rev. ${lookup.find((item: any) => item.id === id)?.sequence}`;
  };

  const buildData = (title: string, value?: string | number | null) => (
    <Row>
      <Col lg={5} className="fw-bold">
        {title}
      </Col>
      <Col className="text-end">{value ?? ''}</Col>
    </Row>
  );

  const tableRef = useRef<TableViewHandle>(null);
  const columns = useMemo(() => {
    return columnsFactory(shifts, calendarFrequencies, filteredServices || []);
  }, [shifts, calendarFrequencies, filteredServices]);

  return (
    <div className="budget-review">
      {/* HEADER INFO */}
      <div className="review-info">
        <Col className="info-section">{buildData('Client:', `${client?.full_name ?? ''}`)}</Col>
        <Col className="info-section">
          {buildData(
            'Price Plan:',
            `${findAndBuild(pricePlans, price_plan_id)}, 
            ${findAndBuildRevision(pricePlanRevisions, price_plan_revision_id)}`,
          )}
        </Col>
        <Col className="info-section">
          {buildData('Start Date:', start_date ? dayjs(start_date).format(dateDisplayFormat) : '')}
          {buildData('End Date:', end_date ? dayjs(end_date).format(dateDisplayFormat) : '')}
        </Col>
      </div>

      {/* SECTIONS */}
      <div className="budget-container">
        {Array.isArray(sections) && sections.length > 0 ? (
          sections.map((section: Section) => {
            const { category_id, category, contrib_proportion } = section;
            const items = (section.items ?? []).filter((item) => item.copy_status !== COPY_STATUS.REMOVED);

            return (
              <div key={category_id}>
                <hr className="section-split-line" />
                <div className="budget-section final">
                  <div className="section-header final">
                    <Row className="w-100">{buildData('Category:', category?.name)}</Row>
                    <Row className="w-100">{buildData('Co-Contribution:', `${contrib_proportion ?? 0}%`)}</Row>
                  </div>

                  <div className="budget-table-wrapper final">
                    <TableView
                      ref={tableRef}
                      id={category_id}
                      data={items}
                      columns={columns}
                      context={{ category_id, start_date, end_date }}
                      emptyMessage="No items found"
                      readonly
                      striped
                    />
                  </div>
                  <div className="section-totals-container">
                    <SectionTotal category_id={category_id} section={section} />
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-muted">No sections available</div>
        )}

        {/* GRAND TOTAL */}
        <div className="totals-container">
          <BudgetSupplementTotal id="supplements-review-grand-total" supplements={supplements} />
          <BudgetGrandTotal
            id="review-grand-total"
            showCaseManagement={showCaseManagement({ package_fixed_id })}
            totals={grand_total}
            amount={total_amount}
            supplements={supplements}
            includesCaseManagement={total_includes_case_management}
          />
        </div>
      </div>
    </div>
  );
};

export default BudgetReview;
